import { keepAliveSize, type TerminalSize } from './keepalive';

/**
 * The PTY's output is NOT replayed to a stream that attaches late: neither the
 * sandbox daemon nor the backend buffers it. A stream that opens after a failed
 * or closed attempt has therefore missed whatever the program printed in the
 * gap, and a full-screen program (Claude Code) will not repaint by itself.
 * Changing the PTY size delivers SIGWINCH, which such programs redraw on.
 */
export interface ReattachInput {
  /** The hook option `redrawOnReattach`. */
  enabled: boolean;
  /** True when this stream open follows at least one failed/closed stream for the same session. */
  followsFailedAttempt: boolean;
  /** Last size the PTY was told (null when never measured). */
  lastSize: TerminalSize | null;
}

/**
 * Whether this stream open should force a repaint, and with which two sizes:
 * `nudge` (one row shorter) first, then `restore` (the real size). Null means
 * do nothing. Never goes below 1 row and never yields a 0x0 / invalid size.
 */
export function reattachRepaintSizes(
  input: ReattachInput,
): { nudge: TerminalSize; restore: TerminalSize } | null {
  if (!input.enabled || !input.followsFailedAttempt) return null;
  const size = keepAliveSize(input.lastSize);
  if (!size) return null;
  const nudgeRows = size.rows > 1 ? size.rows - 1 : size.rows + 1;
  return { nudge: { cols: size.cols, rows: nudgeRows }, restore: size };
}
